import type { FunctionComponent } from 'preact'
import register from 'preact-custom-element'
import type { Ref } from 'preact/hooks'
import { useEffect, useRef, useState } from 'preact/hooks'

/**
 * Turns a Preact component into a Web Component,
 * allowing the component to be used in either context
 * @param func A (P)React function component
 * @param tagName The Web Component tag name, like 'my-component'
 * @param observedAttributes Array of element attributes to observe for changes
 * @param options Additional web component options
 * @returns The custom element
 */
export default function asWebComponent(
    func: FunctionComponent<any>,
    tagName: string,
    observedAttributes?: string[],
    options?: { shadow: boolean }
) {
    if (customElements.get(tagName)) {
        return customElements.get(tagName)
    }
    register(func, tagName, observedAttributes, options)
    return customElements.get(tagName)
}

interface useCustomElementReturn {
    immediateChildRef: Ref<any>
    customElement: HTMLElement | null | undefined
}

/**
 * Allows access to the Web Component wrapper, from within the Preact Component
 */
export function useCustomElement(): useCustomElementReturn {
    const ref = useRef<any>(null)
    const [customElement, setCustomElement] = useState(undefined)
    useEffect(() => {
        setCustomElement(ref.current?.parentElement)
    }, [ref])

    return { immediateChildRef: ref, customElement }
}
